import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { fetchClientServices } from '../widget-api/client-services';
import { useAsync } from '../widget-hooks/useAsync';
import { useEmbedOrgBranch } from '../widget-hooks/useEmbedOrgBranch';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { formatPhone, telHref } from '../widget-lib/phone-tel';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { branchHoursLiveHint } from '../widget-lib/branch-hours-status';
import { yandexMapsSearchUrl } from '../widget-lib/yandex-maps-url';
import { PATH } from '../widget-lib/path-routes';
import { RichTextBlock } from '../widget-ui/rich-text-block';

function formatPrice(p: string | number): string {
  try {
    const n = typeof p === 'number' ? p : Number.parseFloat(p);
    if (Number.isFinite(n) && n > 0) {
      return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);
    }
  } catch { /* ignore */ }
  return '';
}

function formatDuration(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60} ч`;
  if (minutes > 60) return `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
  return `${minutes} мин`;
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function WalletIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M16 12a2 2 0 1 1 4 0v2a2 2 0 1 1-4 0v-2z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M2 9h20" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" fill="currentColor"/>
    </svg>
  );
}

function TicketIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M9 12h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function ServiceDetailPage() {
  const { serviceId: serviceIdParam } = useParams();
  const [, setSearchParams] = useSearchParams();
  const suffix = useQuerySuffix();
  const { organizationId, accessToken } = useEmbedBootstrap();
  const { widgetConfig, guestShowcaseOrg, needsBranch, effectiveBranchId } = useEmbedOrgBranch();

  const serviceId = serviceIdParam != null ? Number.parseInt(serviceIdParam, 10) : NaN;
  const validId = Number.isFinite(serviceId) && serviceId > 0;

  const orgId = widgetConfig?.organization_id ?? organizationId;
  const guestBranches = guestShowcaseOrg?.branches ?? [];

  const canLoad =
    orgId != null &&
    !needsBranch &&
    (effectiveBranchId != null ||
      (widgetConfig != null && (widgetConfig.branches?.length ?? 0) === 0) ||
      (guestShowcaseOrg != null && guestBranches.length === 0) ||
      (widgetConfig == null && guestShowcaseOrg == null && accessToken != null));

  const { data: services, loading, error } = useAsync(
    () => {
      if (orgId == null || needsBranch || !canLoad || !validId) return Promise.resolve([]);
      return fetchClientServices({ organizationId: orgId, branchId: effectiveBranchId, accessToken });
    },
    [orgId, needsBranch, effectiveBranchId, accessToken, canLoad, validId]
  );

  const service = services?.find((s) => s.id === serviceId) ?? null;

  useWidgetSeo(
    service?.name || 'Услуга',
    service?.description || 'Информация об услуге, стоимость, длительность и онлайн-запись.'
  );

  if (!validId) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Некорректный идентификатор услуги.</p>
      </div>
    );
  }

  if (needsBranch && (widgetConfig || guestShowcaseOrg)) {
    const branches = widgetConfig?.branches ?? guestShowcaseOrg?.branches ?? [];
    return (
      <div className="space-y-4 w-full min-w-0 px-4 pt-4 pb-4 sm:px-6">
        <Link
          className="inline-flex items-center gap-1 text-sm text-[var(--accent)] underline-offset-2 hover:underline"
          to={`${PATH.services}${suffix}`}
        >
          ← Все услуги
        </Link>
        <h1 className="text-xl font-semibold tracking-tight">Услуга</h1>
        <p className="text-sm text-[var(--text-muted)]">Выберите филиал.</p>
        <ul className="flex flex-col gap-2">
          {branches.map((b) => (
            <li key={b.id}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('branch_id', String(b.id));
                    return next;
                  });
                }}
              >
                {b.name}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (orgId == null || !canLoad) {
    return (
      <div className="space-y-3 w-full min-w-0 px-4 pt-4 sm:px-6">
        <p className="text-sm text-[var(--text-muted)]">Нет контекста организации для загрузки услуги.</p>
        <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.services}${suffix}`}>
          К списку услуг
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-[var(--shadow-card)]">
          <div className="h-44 w-full animate-pulse rounded-xl bg-[var(--bg-soft)] sm:h-56" />
          <div className="mt-4 space-y-2">
            <div className="h-6 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
            <div className="h-4 w-full animate-pulse rounded bg-[var(--bg-soft)]" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-[var(--bg-soft)]" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-w-0 px-4 pt-4 sm:px-6">
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="space-y-3 w-full min-w-0 px-4 pt-4 sm:px-6">
        <p className="text-sm text-[var(--text-muted)]">Услуга не найдена.</p>
        <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.services}${suffix}`}>
          К списку услуг
        </Link>
      </div>
    );
  }

  const branches = widgetConfig?.branches ?? guestShowcaseOrg?.branches ?? [];
  const branchItem = effectiveBranchId != null
    ? branches.find((b) => b.id === effectiveBranchId) ?? null
    : null;
  const servicePhotoUrl = displayableMediaUrl(service.photo_url);
  const baseVal = Number.parseFloat(String(service.price));
  const fromVal = typeof service.price_from === 'number' ? service.price_from : null;
  const useFrom = fromVal != null && Number.isFinite(baseVal) && fromVal < baseVal;
  const priceStr = useFrom ? `от ${formatPrice(fromVal as number)}` : formatPrice(service.price);
  const trialVal =
    typeof service.trial_price === 'number' && Number.isFinite(service.trial_price) && service.trial_price >= 1
      ? service.trial_price
      : null;
  const durationStr = formatDuration(service.duration_minutes);
  const addr = branchItem?.address?.trim() ?? '';
  const mapHref = addr ? yandexMapsSearchUrl(addr) : null;
  const tz = (widgetConfig?.timezone ?? '').trim() || 'Europe/Moscow';
  const hint = branchItem?.working_hours ? branchHoursLiveHint(branchItem.working_hours, tz, new Date()) : null;
  const isOpen = hint?.kind === 'open' || hint?.kind === 'closes_in';
  const hintText =
    hint?.kind === 'closes_in'
      ? `Закроется через ${hint.minutes} мин.`
      : hint?.kind === 'open'
        ? 'Работает сейчас'
        : null;
  const paymentText = service.payment_required ? 'Картой или наличными' : 'Предоплата не требуется';

  return (
    <div className="flex flex-1 flex-col pb-24 pt-0 sm:pb-28">
      {/* Hero */}
      <div className="relative overflow-hidden">
        {servicePhotoUrl ? (
          <div className="relative h-52 w-full sm:h-64 lg:h-72">
            <img
              src={servicePhotoUrl}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="h-52 w-full sm:h-64 lg:h-72" style={{ background: 'linear-gradient(135deg, var(--btn-primary-bg) 0%, var(--accent) 50%, var(--focus-ring) 100%)' }} />
        )}

        <Link
          className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-black/40 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-black/55 sm:left-4 sm:top-4 sm:px-4 sm:py-1.5 sm:text-sm"
          to={`${PATH.services}${suffix}`}
        >
          ← Все услуги
        </Link>

        <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 sm:px-4 sm:pb-5">
          <div className={`inline-flex rounded-full px-4 py-1.5 backdrop-blur-sm sm:px-5 sm:py-2 ${servicePhotoUrl ? 'bg-white/40' : 'bg-white/20'}`}>
            <h1 className="text-lg font-light tracking-tight text-[var(--text)] sm:text-xl">{service.name}</h1>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5 px-4 pt-5 sm:px-6 sm:pt-6">
        {/* Quick info row */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-[var(--accent)]" />
            <span className="text-base font-semibold text-[var(--text)]">{durationStr}</span>
          </div>
          {priceStr ? (
            <div className="flex items-center gap-2">
              <WalletIcon className="h-6 w-6 text-[var(--accent)]" />
              <span className="text-xl font-bold text-[var(--text)]">{priceStr}</span>
            </div>
          ) : null}
          {trialVal != null ? (
            <div className="flex items-center gap-2">
              <TicketIcon className="h-5 w-5 text-[var(--accent)]" />
              <span className="text-sm font-semibold text-[var(--accent)]">
                Пробная цена: {formatPrice(trialVal)}
              </span>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <TicketIcon className="h-5 w-5 text-[var(--accent)]" />
            <span className="text-sm font-medium text-[var(--text-muted)]">{paymentText}</span>
          </div>
        </div>

        {/* Description */}
        {service.description ? (
          <section className="rounded-2xl bg-[var(--bg-elevated)] px-5 py-5 shadow-[var(--shadow-card)] sm:px-6 sm:py-6">
            <RichTextBlock html={service.description} format={service.description_format} />
          </section>
        ) : null}

        {/* Branch card */}
        {branchItem && (addr || branchItem.working_hours) ? (
          <section className="rounded-2xl bg-[var(--bg-elevated)] px-5 py-5 shadow-[var(--shadow-card)] sm:px-6 sm:py-6">
            <div className="space-y-4">
              {addr ? (
                <div className="flex items-start gap-3">
                  <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--accent)]" />
                  <div className="min-w-0 flex-1">
                    <a
                      href={mapHref ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-[var(--text)] underline-offset-2 hover:text-[var(--accent)] hover:underline"
                    >
                      {addr}
                    </a>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {hintText ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isOpen ? 'text-green-600 dark:text-green-400' : 'text-[var(--text-muted)]'}`}>
                          <span className={`inline-block h-2 w-2 rounded-full ${isOpen ? 'bg-green-500' : 'bg-[var(--text-muted)]'}`} />
                          {hintText}
                        </span>
                      ) : null}
                      {mapHref ? (
                        <a
                          href={mapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[var(--accent)] underline-offset-2 hover:underline"
                        >
                          Построить маршрут
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
              {branchItem.phone ? (
                <div className="flex items-center gap-3">
                  <a
                    href={telHref(branchItem.phone)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-4 text-sm font-medium text-[var(--text)] transition-colors hover:bg-[var(--border)] active:scale-[0.98]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    </svg>
                    {formatPhone(branchItem.phone)}
                  </a>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* Sticky CTA */}
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] sm:px-6">
          <Link
            to={`${PATH.schedule}?service_id=${serviceId}${suffix ? `&${suffix.slice(1)}` : ''}`}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-bg)] px-5 py-3.5 text-base font-semibold text-[var(--accent-fg)] shadow-sm transition-all hover:bg-[var(--accent-bg-hover)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            Забронировать время
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
